import { X } from 'lucide-react'
import { track } from '../lib/analytics'
import { markHintDismissed } from '../lib/searchHints'

// A one-line nudge attached to the bottom of the search box, desktop only,
// dismissible forever.
//
// WHY IT EXISTS (Andrew, 9/20): search is barely used. Over two months, once
// Andrew's own browsers were separated out, genuine visitors ran roughly 25-30
// searches in total. The people who DO search already know what they're doing —
// almost everything they type is a proper noun, either a venue (palmera house,
// sattva yoga, super rad arcade bar) or a performer (sister hazel, mozart, and a
// local act, all searched on one evening). So this isn't here to help searchers.
// It's here to tell the other 99% that the box does what the checkboxes can't.
//
// THE EXAMPLES ARE NOT CLICKABLE, on purpose (Andrew, 9/20). Making them run the
// search was the first cut and it's the wrong trade: a click teaches that this
// box has two buttons under it, where the goal is that somebody puts their hands
// on the keyboard and finds out the box takes anything. So they're emphasis, in
// brick, with NO underline — underline reads as a link and invites the click
// we're deliberately not offering.
//
// ATTACHED, not floating (Andrew, 9/20): no gap, no top border, bottom corners
// rounded and the top ones square, so it reads as part of the input rather than
// a separate notice. The input drops its own bottom radius to meet it, which is
// why `dismissed` lives in FilterDrawer and not in here — both halves have to
// agree about whether this is showing.
//
// DESKTOP ONLY, and measured rather than assumed: desktop is 2,435 pageviews
// from 1,770 people against mobile's 1,608 from 887. `lg` is the breakpoint
// where the drawer stops being an overlay and becomes its own column (see
// FilterDrawer), so it's the one that means "there's room for this".
//
// "your favorite venue or band" stays generic on purpose. Naming one venue in
// the UI would be free placement for that venue, which isn't a thing to hand out
// by accident on a calendar that sells sponsor slots.

interface Props {
  /** Terms with something actually on (lib/searchHints). */
  examples: string[]
  /** Parent owns the flag, because the input's corners depend on it too. */
  onDismiss: () => void
}

export function SearchHint({ examples, onDismiss }: Props) {
  return (
    // border-t-0 rather than a negative margin: the input's own bottom border is
    // the divider between the two, so adding one here would draw it twice.
    <div className="hidden items-start gap-1.5 rounded-b-md border border-t-0 border-hairline bg-white px-2 py-1.5 lg:flex dark:border-slate-700 dark:bg-slate-900">
      {/* text-sm, not the text-xs the no-matches line uses: this is descriptive
          copy meant to be read by someone who hasn't engaged yet, where that one
          is feedback on something they already did. On this WHITE fill slate-600
          measures 7.58:1 and brick-700 10.10:1; on the dark slate-900 fill,
          slate-300 is 12.02:1 and slate-100 16.30:1. */}
      <p className="min-w-0 flex-1 text-sm leading-snug text-slate-600 dark:text-slate-300">
        Search for{' '}
        {examples.map((term, i) => (
          <span key={term}>
            {i > 0 && ', '}
            <span className="font-semibold text-brick-700 dark:text-slate-100">{term}</span>
          </span>
        ))}
        , or your favorite venue or band.
      </p>
      <button
        type="button"
        onClick={() => {
          track('search_hint_dismissed')
          markHintDismissed()
          onDismiss()
        }}
        aria-label="Dismiss search tip"
        // h-3 rather than the usual h-2 start: an 8px X is under the size a
        // pointer can reliably land on. The p-1 wrapper carries the hit area.
        //
        // slate-500, not the slate-400 used for quiet icons elsewhere: on a white
        // fill slate-400 measures 2.56:1, under the 3:1 WCAG 1.4.11 asks of a
        // control. slate-500 is 4.76:1, and slate-400 on slate-900 is 6.96:1.
        className="-m-1 shrink-0 p-1 text-slate-500 transition hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"
      >
        <X className="h-3 w-3" />
      </button>
    </div>
  )
}
